export type StationStatus = 'Active' | 'Inactive' | 'Closed';

/**
 * Canonical status text for UI (optionset may send "In Active", "in_active", etc.).
 */
export function displayStationStatusLabel(raw: string): string {
  const s = String(raw ?? '').trim();
  if (!s) return s;
  if (s.toLowerCase() === 'all') return 'All';
  const n = s.toLowerCase().replace(/[\s_]+/g, '');
  if (n === 'inactive') return 'Inactive';
  if (n === 'active') return 'Active';
  if (n === 'closed') return 'Closed';
  return s;
}

/**
 * Optionset labels may differ from {@link StationStatus} spelling; compare normalized (case, spaces, underscores).
 */
export function statusLabelMatchesStation(filterLabel: string, cardStatus: StationStatus): boolean {
  const n = (s: string) => s.trim().toLowerCase().replace(/[\s_]+/g, '');
  return n(filterLabel) === n(cardStatus);
}

export type Analyst = {
  id: string;
  name: string;
};

export type StationCard = {
  id: string;
  /** API airport identifier; use this for GET /api/v1/airport/<id> when present. */
  airport_id?: string;
  stationName: string;
  /** Raw country code from API (e.g. US, CA), when provided. */
  countryCode?: string;
  country: 'USA' | 'Canada';
  status: StationStatus;
  allowancePercent: number;
  analystId: string;
  /** Location/city code e.g. ATL. */
  locationCode?: string;
  /** Display as "countryName / stateName" on card (from API). */
  countryName?: string;
  stateName?: string;
  /** Assignee display name; falls back to analyst name if not set. */
  assigneeName?: string;
  /** Notes text shown in card NOTES section. */
  notes?: string;
  /** For Edit Airport form. */
  iataCode?: string;
  longitude?: string;
  latitude?: string;
  /** Miles — from GET /api/v1/airport / analysis; used for establishment search radius. */
  search_radius_miles?: string;
};

export type Kpi = {
  key: 'Stations' | 'Analysts' | 'USA' | 'Canada';
  value: number;
  active: number;
  inactive: number;
  icon: string;
};

export type OverviewFilters = {
  country: 'All' | 'USA' | 'Canada';
  analystId: 'All' | string;
  /** Optionset label or {@link StationStatus}; display via {@link displayStationStatusLabel}. */
  status: 'All' | StationStatus | string;
  search: string;
};

/** Chart bucket: label + value (no colors; FE uses default light nude shades) */
export type ChartBucket = { label: string; value: number };

/** API1: dashboard summary (KPIs + all 3 charts). Chart data as array of { label, value }. */
export type OverviewDashboardResponse = {
  kpis: Array<{ title: string; value: number; active: number; inactive: number; icon: string }>;
  analysts: Analyst[];
  airportDistribution: ChartBucket[];
  allowanceDistribution: ChartBucket[];
  countrySplit: ChartBucket[];
};

/** API2: cards list (FE does filter + pagination) */
export type OverviewCardsResponse = {
  cards: StationCard[];
};

/** Option item from get_optionset API */
export type OptionItem = {
  label: string;
  value: string;
};

/**
 * Remove Country optionset entries whose label or value is UNKNOWN (any casing).
 * Use for all Country dropdowns fed from get_optionset.
 */
export function filterUnknownCountryOptionsetItems(items: OptionItem[] | undefined | null): OptionItem[] {
  if (!Array.isArray(items)) return [];
  return items.filter((o) => {
    const label = String(o.label ?? '').trim();
    const value = String(o.value ?? '').trim();
    if (label.toUpperCase() === 'UNKNOWN') return false;
    if (value.toUpperCase() === 'UNKNOWN') return false;
    return true;
  });
}

function firstStringField(o: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (v == null) continue;
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return '';
}

/**
 * Map a single optionset row from POST /common/get_optionset into { label, value }.
 * Handles alternate API keys (e.g. name/id vs label/value).
 */
export function normalizeOptionsetItem(raw: unknown): OptionItem | null {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const s = raw.trim();
    return s ? { label: s, value: s } : null;
  }
  if (typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const label = firstStringField(o, ['label', 'name', 'text', 'display_name', 'displayName', 'title']);
  const value = firstStringField(o, [
    'value',
    'id',
    'code',
    'key',
    'assignee_id',
    'user_id',
    'analyst_id'
  ]);
  if (!label && !value) return null;
  return {
    label: label || value,
    value: value || label
  };
}

export function normalizeOptionsetItemList(items: unknown[]): OptionItem[] {
  if (!Array.isArray(items)) return [];
  return items.map(normalizeOptionsetItem).filter((x): x is OptionItem => x != null);
}

/**
 * User_Role optionset: `get_users.role` is often a numeric id while API rows may also expose a text `value`.
 * Prefer `id` (and similar keys) as the stored value so table labels resolve correctly.
 */
export function normalizeUserRoleOptionsetItem(raw: unknown): OptionItem | null {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const s = raw.trim();
    return s ? { label: s, value: s } : null;
  }
  if (typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const label = firstStringField(o, ['label', 'name', 'text', 'display_name', 'displayName', 'title']);
  const id = firstStringField(o, ['id', 'assignee_id', 'user_id', 'analyst_id']);
  const textValue = firstStringField(o, ['value', 'code', 'key']);
  const matchValue = id || textValue || label;
  if (!matchValue && !label) return null;
  return {
    label: label || matchValue,
    value: matchValue
  };
}

export function normalizeUserRoleOptionsetItemList(items: unknown[]): OptionItem[] {
  if (!Array.isArray(items)) return [];
  return items.map(normalizeUserRoleOptionsetItem).filter((x): x is OptionItem => x != null);
}

/** Optionset name for POST /api/v1/common/get_optionset (Comp Sheet Format airport field). */
export const OPTIONSET_NAME_COMP_SHEET_FORMAT = 'Comp_Sheet_Format';

/** Optionset name for user Organization Name field (Add/Edit User). */
export const OPTIONSET_NAME_SSP_REGIONS = 'SSP_REGIONS';

/** Optionset name for user Title / access role field (Add/Edit User). */
export const OPTIONSET_NAME_USER_ROLE = 'User_Role';

/** Region + Title options for Add/Edit User dialogs (single get_optionset call). */
export type AddEditUserOptionsets = {
  regions: OptionItem[];
  userRoles: OptionItem[];
};

/** Application access role (Admin vs Analyst) — mirrors auth.service without importing it. */
export type UserAccessRole = 'admin' | 'analyst';

/** Normalize API role strings to Admin or Analyst when recognized. */
export function normalizeUserAccessRole(raw: string | null | undefined): UserAccessRole | null {
  const s = String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  if (!s) return null;
  if (s === 'admin' || s === 'administrator') return 'admin';
  if (s === 'analyst') return 'analyst';
  if (s.includes('analyst')) return 'analyst';
  if (s.includes('admin')) return 'admin';
  return null;
}

/**
 * Resolve Admin/Analyst from stored `role` (text or optionset id) using User_Role options when needed.
 */
export function resolveUserAccessRole(
  roleRaw: string | null | undefined,
  options: OptionItem[] = []
): UserAccessRole | null {
  const raw = String(roleRaw ?? '').trim();
  if (!raw) return null;
  const direct = normalizeUserAccessRole(raw);
  if (direct) return direct;
  if (!options.length) return null;
  const label = labelFromOptionsetSelection(options, raw);
  return normalizeUserAccessRole(label);
}

/** Ensures stored org string appears in the SSP_REGIONS select (value or label match). */
export function mergeRegionOptionsWithCurrentValue(
  options: OptionItem[],
  currentOrg: string | null | undefined
): OptionItem[] {
  const t = String(currentOrg ?? '').trim();
  const items = options.map((o) => ({
    label: String(o.label ?? o.value ?? '').trim(),
    value: String(o.value ?? o.label ?? '').trim()
  })).filter((o) => o.value.length > 0);
  if (!t) return items;
  const hit = items.some((o) => o.value === t || o.label === t || o.label.toLowerCase() === t.toLowerCase());
  if (hit) return items;
  return [...items, { label: t, value: t }].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
  );
}

/**
 * Pick an optionset array from get_optionset `data` by exact `name` or case/underscore-insensitive key match.
 */
export function optionsetListByName(data: Record<string, OptionItem[]>, name: string): OptionItem[] {
  if (Array.isArray(data[name])) return data[name]!;
  const norm = (s: string) => s.toLowerCase().replace(/_/g, '');
  const target = norm(name);
  const key = Object.keys(data).find((k) => norm(k) === target);
  return key != null && Array.isArray(data[key]) ? (data[key] as OptionItem[]) : [];
}

function normOptionsetMatchKey(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Resolve the optionset `value` for the control (form/API may send `value`, `label`, or a case/space variant).
 */
export function valueFromOptionsetSelection(options: OptionItem[], selected: string): string {
  const t = selected.trim();
  if (!t) return '';
  const byValue = options.find((o) => String(o.value ?? '').trim() === t);
  if (byValue) return String(byValue.value).trim();
  const byLabel = options.find((o) => String(o.label ?? '').trim() === t);
  if (byLabel) return String(byLabel.value).trim();
  const tKey = normOptionsetMatchKey(t);
  const byValueLoose = options.find((o) => normOptionsetMatchKey(String(o.value ?? '')) === tKey);
  if (byValueLoose) return String(byValueLoose.value).trim();
  const byLabelLoose = options.find((o) => normOptionsetMatchKey(String(o.label ?? '')) === tKey);
  if (byLabelLoose) return String(byLabelLoose.value).trim();
  return t;
}

/** Resolve the display **label** for an optionset `value`, or return `selected` if already a label / unknown. */
export function labelFromOptionsetSelection(options: OptionItem[], selectedValueOrLabel: string): string {
  const t = String(selectedValueOrLabel ?? '').trim();
  if (!t) return '';
  const byValue = options.find((o) => String(o.value ?? '').trim() === t);
  if (byValue) {
    return String(byValue.label ?? byValue.value).trim();
  }
  const byLabel = options.find((o) => String(o.label ?? '').trim() === t);
  if (byLabel) {
    return String(byLabel.label ?? byLabel.value).trim();
  }
  const tKey = normOptionsetMatchKey(t);
  const byValueLoose = options.find((o) => normOptionsetMatchKey(String(o.value ?? '')) === tKey);
  if (byValueLoose) {
    return String(byValueLoose.label ?? byValueLoose.value).trim();
  }
  const byLabelLoose = options.find((o) => normOptionsetMatchKey(String(o.label ?? '')) === tKey);
  if (byLabelLoose) {
    return String(byLabelLoose.label ?? byLabelLoose.value).trim();
  }
  return t;
}

/** Request for POST /api/v1/common/get_optionset */
export type OptionsetRequest = {
  names: string[];
  /** When present, backend may scope options (e.g. cuisines per airport). */
  airport_code?: string;
  /** Price filter default for scoping options; use `null` for unset (not `"All"`). */
  price?: string | null;
  [key: string]: unknown;
};

/** Response from POST /api/v1/common/get_optionset */
export type OptionsetResponse = {
  status: string;
  data: Record<string, OptionItem[]>;
};

/** Filter options fetched from get_optionset API */
export type FilterOptions = {
  countries: OptionItem[];
  analysts: OptionItem[];
  statuses: OptionItem[];
};

/** POST /api/v1/airport/overview/cards and /graphs — filter body (empty array = no filter on that dimension). */
export type OverviewFilterPayload = {
  country: string[];
  analyst: string[];
};

